import { useMutation, useSubscription } from '@apollo/client/react'
import {
  Avatar,
  Badge,
  Box,
  Button,
  chakra,
  Separator,
  Heading,
  HStack,
  Icon,
  Spinner,
  VStack,
  Wrap,
  Presence,
} from '@chakra-ui/react'
import { ButtonLink } from '@src/components/sco/ButtonLink.tsx'
import { UserSearchForm } from '@src/components/sco/Matching/UserSearchForm'
import { USER_SEARCH_SUBSCRIPTION_FRAGMENT } from '@src/components/sco/Matching/userSearchFragment'
import { TypingText } from '@src/components/sco/TypingText'
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider'
import { ScoUserMatch, ScoUserSearch, UpsertUserSearchInput } from '@src/graphql-types'
import { gql } from '@src/lib/gql'
import React, { useEffect, useState } from 'react'
import { LuChevronRight, LuRadar, LuRefreshCw } from 'react-icons/lu'

/*
 1 = queued
 3 = expanding
 5 = expanding + title
 6 = expanding + skills
 7 = expanding + processingDescription update
 8 = matching
 9 = matching + count
 10 = scoring
 11 = complete
 */
const DEV_PRESET = 0

export const UserSearchLoadingState = (props: {
  onReady: () => any
  userSearch: ScoUserSearch
  reprocessIfEmpty?: boolean
  viewMatchesHref?: string
}) => {
  const [processingMessages, setProcessingMessages] = useState<string[]>([])
  const [userMatches, setUserMatches] = useState<ScoUserMatch[]>([])
  const subscription = useSubscription(
    gql`
      subscription ($userSearchId: String!) {
        scoUserSearchUpdated(userSearchId: $userSearchId) {
          userSearch {
            id
            ${USER_SEARCH_SUBSCRIPTION_FRAGMENT}
          }
        }
      }
    `,
    {
      skip: DEV_PRESET > 0,
      variables: {
        userSearchId: props.userSearch.userSearchId,
      },
    }
  )

  const [upsertUserSearch] = useMutation(gql`
    mutation ($input: UpsertUserSearchInput!) {
      upsertUserSearch(input: $input) {
        userSearch {
          id
          userSearchId
          processingStatus
        }
      }
    }
  `)

  const userSearch = props.userSearch

  useEffect(() => {
    if (
      userSearch?.userSearchId &&
      props.reprocessIfEmpty &&
      (!userSearch.processingStatus || userSearch.processingStatus === 'pending')
    ) {
      const input: UpsertUserSearchInput = {
        userSearchId: userSearch.userSearchId,
        reprocess: true,
      }
      upsertUserSearch({
        variables: {
          input,
        },
      })
    }
  }, [props.reprocessIfEmpty, userSearch?.userSearchId])

  useEffect(() => {
    if (subscription.data?.scoUserSearchUpdated?.userSearch?.processingStatus === 'complete') {
      props.onReady()
    }
  }, [subscription.data?.scoUserSearchUpdated?.userSearch?.processingStatus])

  useEffect(() => {
    if (props.userSearch.userMatches.items.length === 0) {
      setUserMatches([])
    } else if (userMatches?.length === 0 && props.userSearch.userMatches.items.length > 0) {
      setUserMatches(props.userSearch.userMatches.items)
    }
  }, [props.userSearch.userMatches?.items?.length])

  useEffect(() => {
    if (userSearch.processingStatus === 'queued') {
      setProcessingMessages(['Queued! Will process shortly...'])
    } else if (
      userSearch?.processingDescription &&
      processingMessages[processingMessages.length - 1] !== userSearch?.processingDescription
    ) {
      setProcessingMessages(prev => [...prev, userSearch.processingDescription])
    }
  }, [userSearch?.updatedAt])

  const { getBreakpointValue } = useBreakpointContext()

  if (!userSearch.processingStatus) {
    return (
      <>
        <UserSearchForm userSearch={userSearch} />
      </>
    )
  }

  return (
    <Box
      // bgColor={'sidebarBackground'}
      borderRadius={'lg'}
      margin={'auto'}
      maxWidth={'1024px'}
      overflowY={'auto'}
      padding={'2em'}
      width={'full'}
    >
      <Box color={'white'}>
        <HStack align={'center'} marginBottom={'0.5em'}>
          <Icon asChild color={'mocha'}>
            <LuRadar />
          </Icon>
          <Heading color={'mochaLight'} size={'xs'}>
            {'Status:'}{' '}
            <PulsatingText isActive={userSearch.processingStatus !== 'complete'}>
              {userSearch.processingStatus}
            </PulsatingText>
          </Heading>
        </HStack>
        <Box fontSize={getBreakpointValue(['lg', 'lg', 'xl'])} fontWeight={'600'} letterSpacing={'-0.05em'}>
          <TypingText delay={5}>{userSearch.processingDescription}</TypingText>
        </Box>
      </Box>
      <Separator marginY={'1em'} />
      <VStack align={'stretch'} gap={10} justify={'stretch'}>
        <Box
        // bgGradient={'linear-gradient(to-b, backgroundTertiary, transparent)'}
        // padding={6}
        // borderRadius={'xl'}
        >
          <Wrap align={'center'} gap={1}>
            <Heading size={getBreakpointValue(['md', 'md', ''])}>Skills:</Heading>
            {userSearch.requiredSkills?.map((requiredSkill, idx) => (
              <Presence
                key={idx}
                animationDuration={'moderate'}
                delay={idx / 100}
                present={true}
                animationName={{
                  _open: 'fade-in',
                  _closed: 'fade-out',
                }}
              >
                <Badge opacity={parseInt(requiredSkill[1]) / 100} variant={'solid'}>
                  {requiredSkill[0]}
                </Badge>
              </Presence>
            ))}
            {userSearch.relevantSkills?.length === 0 && <Spinner />}
            {userSearch.relevantSkills?.map((relevantSkill, idx) => (
              <Presence
                key={idx}
                animationDuration={'moderate'}
                delay={idx / 100}
                present={true}
                animationName={{
                  _open: 'fade-in',
                  _closed: 'fade-out',
                }}
              >
                <Badge
                  bgColor={'colorPalette.emphasized/50'}
                  opacity={parseInt(relevantSkill[1]) / 100}
                  variant={'solid'}
                >
                  {relevantSkill[0]}
                  {' @ '}
                  {relevantSkill[1]}%
                </Badge>
              </Presence>
            ))}
          </Wrap>
        </Box>
        {/* <Separator marginY={'1em'} /> */}
        {userSearch.userMatches?.pagination?.itemCount > 0 && (
          <Box bg={'colorPalette.container'} borderRadius={'2xl'} padding={8}>
            <HStack align={'center'} justify={'space-between'} width={'full'}>
              <Heading size={'lg'}>{userSearch.userMatches?.pagination?.itemCount} Matches Found!</Heading>
              <ButtonLink
                asChild
                href={props.viewMatchesHref || `/members/matching/${userSearch.userSearchId}`}
                size={'sm'}
                variant={'cta'}
              >
                View My Matches
                <LuChevronRight />
              </ButtonLink>
            </HStack>
            <Separator marginY={6} />
            <Wrap>
              {userSearch.userMatches?.items?.map(userMatch => (
                <Avatar.Root key={userMatch?.userMatchId} size={getBreakpointValue(['sm', 'sm', 'md'])}>
                  <Avatar.Fallback name={userMatch.targetUser.fullName} />
                  <Avatar.Image src={userMatch.targetUser.pictureUrl} />
                </Avatar.Root>
              ))}
            </Wrap>
          </Box>
        )}
      </VStack>
      <Box borderTop={'1px solid {colors.colorPalette.border/50}'} marginTop={8} paddingTop={8}>
        <Button
          size={'xs'}
          variant={'outline'}
          onClick={() => {
            const input: UpsertUserSearchInput = {
              userSearchId: userSearch.userSearchId,
              reprocess: true,
            }
            upsertUserSearch({
              variables: {
                input,
              },
            })
          }}
        >
          <LuRefreshCw />
          Refresh Matches
        </Button>
      </Box>
    </Box>
  )
}

export const PulsatingText = ({ children, isActive }) => {
  const [pulsate, setPulsate] = useState(false)

  useEffect(() => {
    const intervalId = setInterval(() => {
      setPulsate(prev => !prev)
    }, 600) // 800ms fade in, 800ms fade out

    if (isActive === false) {
      clearInterval(intervalId)
    }

    return () => clearInterval(intervalId) // Cleanup on unmount
  }, [isActive])

  const opacity = pulsate ? 0.5 : 1

  return (
    <chakra.span
      style={{
        transition: 'opacity 300ms ease-in-out',
        opacity: isActive ? opacity : 1,
      }}
    >
      {children}
    </chakra.span>
  )
}
